import { useEffect, useState } from "react";
import { CircleDollarSign, Plus, Trash2, TrendingUp, TrendingDown } from "lucide-react";
import { api, money, trDate, todayISO } from "../lib/api";
import { PageHeader, Modal, FormField, SubmitButton, useForm, EmptyState, StatMini } from "../components/farm/FarmUI";
import { HERO, HERO_TINT } from "../lib/heroes";

export default function Finance() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const { form, set, reset } = useForm({ date: todayISO(), type: "income", category: "", amount: "", note: "" });

  const load = () => api.get("/finance").then(setItems).catch(() => setError("Yüklenemedi."));
  useEffect(() => { load(); }, []);

  const income = items.filter((r) => r.type === "income").reduce((a, r) => a + Number(r.amount || 0), 0);
  const expense = items.filter((r) => r.type === "expense").reduce((a, r) => a + Number(r.amount || 0), 0);
  const filtered = filter === "all" ? items : items.filter((r) => r.type === filter);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const rec = await api.post("/finance", {
        date: form.date,
        type: form.type,
        category: form.category,
        amount: Number(form.amount) || 0,
        note: form.note,
      });
      setItems((x) => [rec, ...x].sort((a, b) => (b.date || "").localeCompare(a.date || "")));
      reset();
      setOpen(false);
    } catch { setError("Kayıt eklenemedi."); }
  };

  const remove = async (id) => {
    try { await api.del(`/finance/${id}`); setItems((x) => x.filter((i) => i.id !== id)); }
    catch { setError("Silinemedi."); }
  };

  return (
    <>
      <PageHeader
        eyebrow="KASA · GELİR / GİDER"
        title="Finans"
        subtitle="Modül dışı ek gelir ve giderlerinizi buradan işleyin."
        heroImage={HERO.finance}
        heroTint={HERO_TINT.finance}
        actions={
          <button className="primary-btn" data-testid="finance-add-btn" onClick={() => setOpen(true)}>
            <Plus size={17} /> Hareket ekle
          </button>
        }
      />

      {error && <div className="api-error" data-testid="finance-error">{error}</div>}

      <section className="stats-mini-row">
        <StatMini label="Toplam gelir" value={money(income)} tone="green" testId="finance-stat-income" />
        <StatMini label="Toplam gider" value={money(expense)} tone="red" testId="finance-stat-expense" />
        <StatMini label="Net" value={money(income - expense)} tone="teal" testId="finance-stat-net" />
      </section>

      <div className="filter-row">
        <button className={`chip ${filter === "all" ? "active" : ""}`} data-testid="filter-all" onClick={() => setFilter("all")}>Tümü</button>
        <button className={`chip ${filter === "income" ? "active" : ""}`} data-testid="filter-income" onClick={() => setFilter("income")}>Gelir</button>
        <button className={`chip ${filter === "expense" ? "active" : ""}`} data-testid="filter-expense" onClick={() => setFilter("expense")}>Gider</button>
      </div>

      <div className="panel">
        {filtered.length === 0 ? (
          <EmptyState icon={CircleDollarSign} title="Henüz hareket yok" text="Modüller dışında kalan tüm gelir ve giderleri buradan yönetin." actionLabel="İlk kaydı ekle" onAction={() => setOpen(true)} testId="finance-empty-add" />
        ) : (
          <div className="fin-list">
            {filtered.map((r) => (
              <div className="fin-row" key={r.id} data-testid={`finance-item-${r.id}`}>
                <div className={`fin-icon ${r.type}`}>
                  {r.type === "income" ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                </div>
                <div className="fin-body">
                  <b>{r.category || (r.type === "income" ? "Gelir" : "Gider")}</b>
                  <small>{trDate(r.date)}{r.note ? ` · ${r.note}` : ""}</small>
                </div>
                <strong className={r.type === "income" ? "positive" : "negative"}>
                  {r.type === "income" ? "+" : "−"}{money(r.amount)}
                </strong>
                <button className="delete-btn always" data-testid={`finance-delete-${r.id}`} onClick={() => remove(r.id)}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <Modal title="Yeni gelir / gider" subtitle="KASA" onClose={() => setOpen(false)}>
          <form onSubmit={submit}>
            <FormField label="Tür">
              <select required data-testid="finance-form-type" value={form.type} onChange={(e) => set("type", e.target.value)}>
                <option value="income">Gelir</option>
                <option value="expense">Gider</option>
              </select>
            </FormField>
            <FormField label="Kategori / açıklama">
              <input required data-testid="finance-form-category" placeholder="Örn. Traktör tamiri" value={form.category} onChange={(e) => set("category", e.target.value)} />
            </FormField>
            <div className="form-row">
              <FormField label="Tutar (₺)">
                <input required type="number" step="1" data-testid="finance-form-amount" value={form.amount} onChange={(e) => set("amount", e.target.value)} />
              </FormField>
              <FormField label="Tarih">
                <input required type="date" data-testid="finance-form-date" value={form.date} onChange={(e) => set("date", e.target.value)} />
              </FormField>
            </div>
            <FormField label="Not">
              <textarea data-testid="finance-form-note" value={form.note} onChange={(e) => set("note", e.target.value)} />
            </FormField>
            <SubmitButton testId="finance-form-submit">Kaydet</SubmitButton>
          </form>
        </Modal>
      )}
    </>
  );
}
